import React from 'react';
import { Lightbulb, Calendar, Clock } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import type { ServiceSuggestion } from '../utils/serviceSuggestions';

interface Props {
  suggestions: ServiceSuggestion[];
  isLoading: boolean;
}

const confidenceColor: Record<ServiceSuggestion['confidence'], string> = {
  alto: '#16a34a',
  medio: '#d97706',
  baixo: '#b91c1c',
};

const ServiceSuggestionsPanel: React.FC<Props> = ({ suggestions, isLoading }) => {
  const { t, formatDate } = useI18n();

  const getConfidenceLabel = (confidence: ServiceSuggestion['confidence']) =>
    t(`serviceSuggestions.confidence.${confidence}` as const);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
        padding: '20px',
        width: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <Lightbulb color="#facc15" />
        <div>
          <h3 style={{ margin: 0, color: '#0f172a' }}>{t('serviceSuggestions.title')}</h3>
          <p style={{ margin: 0, color: '#475569', fontSize: '13px' }}>
            {t('serviceSuggestions.subtitle')}
          </p>
        </div>
      </div>

      {isLoading ? (
        <p style={{ color: '#475569' }}>{t('serviceSuggestions.loading')}</p>
      ) : suggestions.length === 0 ? (
        <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>
          <p style={{ margin: 0 }}>{t('serviceSuggestions.noSuggestions')}</p>
          <small>{t('serviceSuggestions.noSuggestionsHint')}</small>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.clientId}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '12px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ color: '#0f172a' }}>{suggestion.clientName}</strong>
                <span
                  style={{
                    color: confidenceColor[suggestion.confidence],
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {getConfidenceLabel(suggestion.confidence)}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: '#475569' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Clock size={14} />
                  {t('serviceSuggestions.avgInterval', {
                    days: suggestion.averageIntervalDays,
                  })}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Calendar size={14} />
                  {t('serviceSuggestions.nextSuggested')}{' '}
                  {formatDate(new Date(suggestion.nextSuggestedDate))}
                </span>
                <span>
                  {t('serviceSuggestions.totalCompleted', { count: suggestion.totalCompleted })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceSuggestionsPanel;

